import { useMemo } from 'react';

import {
    canonicalCoin,
    useTokenPairs,
    useTokenPairStore,
} from '@/features/tokenPairs';
import { USDC_DECIMALS } from '@/lib/api/speed';

import type { PnlShareRequest } from '../components/PnlShareDialog';
import { selectActiveTradeVisual, useTradingStore } from '../store';
import type { ClosedTrade } from '../types';
import {
    pnlCardFromClosedTrade,
    pnlCardFromDemoPosition,
    pnlCardFromUserTrade,
} from '../utils/pnlCardData';
import {
    formatTradePrice,
    leverageFromTrade,
    priceAmount,
    resolvePair,
    resolveTerminalStatus,
    RESTING_STATUS_META,
    STATUS_META,
    tokenAmount,
} from '../utils/tradeFormat';
import { timestampMs } from '../utils/tradeTime';

import { useActiveUserTrades } from './useActiveUserTrades';
import type { PriceConfig } from './usePriceConfig';
import { useUserTrades } from './useUserTrades';

/**
 * One line of the positions/history ledger, independent of how it is drawn —
 * the desktop terminal renders these as a seven-column grid, the mobile sheet
 * as stacked cards, and both must be describing the same trades.
 */
export interface TerminalRow {
    key: string;
    /**
     * Open time as ms epoch (null when the record's timestamp is unusable).
     * Rows carry the instant, not a label: the Time column is formatted at
     * render time because its mode (wall clock / "x ago") is a user toggle,
     * and row order depends on this and nothing else.
     */
    at: number | null;
    market: string;
    side: 'LONG' | 'SHORT';
    leverage: string;
    margin: string;
    entry: string;
    pnl: string | null;
    pnlPositive: boolean;
    /** Terminal outcome badge (TP/SL/LIQ/FAIL); absent on a plain close. */
    status?: { label: string; className: string } | null;
    /**
     * Which live PnL cell this row gets. `charted` extrapolates against the
     * streaming price; `offChart` shows the exchange's value for that market.
     * Rows are ordered by open time and never move when the chart does, so a
     * row's live mode changes underneath it while its position does not.
     */
    live?: 'charted' | 'offChart';
    tradeHash?: string;
    coin?: string;
    /**
     * A resting limit order rather than a position: nothing is open, so the row
     * offers cancel / fill-now instead of close, and its PnL cell shows how far
     * the market still is from `limitPrice` instead of a profit.
     */
    resting?: true;
    limitOrigin?: boolean;
    /** the price a resting order rests at; only set on resting rows. */
    limitPrice?: number;
    /** Opens the PnL card; absent on rows with nothing to show (e.g. Failed). */
    share?: PnlShareRequest;
    /**
     * Demo positions live only in the store and are marked to the price feed,
     * so their card is built when the button is pressed — building it here
     * would make these row arrays depend on every ~15Hz tick.
     */
    shareDemo?: true;
}

// Sign goes outside the $ ("-$0.02", not "$-0.02"). Only demo rows exercised
// this before — real-mode PnL never parsed, so every loss now shows here too.
export function formatPnl(value: number): string {
    return `${value >= 0 ? '+' : '-'}$${Math.abs(value).toFixed(2)}`;
}

/**
 * The positions and history ledgers, built once for whichever surface is
 * mounted. Reads the active/closed trade queries in real mode and the local
 * history slice in demo.
 *
 * Rows carry no live PnL: that arrives per-row from a leaf cell, so a ~15Hz
 * price tick re-renders one `<span>` instead of rebuilding every row.
 */
export function useTerminalRows(priceConfig: PriceConfig): {
    positionRows: TerminalRow[];
    historyRows: TerminalRow[];
    limitRows: TerminalRow[];
    historyErrored: boolean;
    /** The share card for a row, built on demand. */
    resolveShare: (row: TerminalRow) => PnlShareRequest | null;
} {
    const isRealMode = useTradingStore((s) => s.runtimeMode === 'real');
    const activePosition = useTradingStore(selectActiveTradeVisual);
    // demo-mode limit orders: they have no server row, so they reach the table
    // the same way the demo position does — straight from the store.
    const demoRestingOrders = useTradingStore((s) => s.demoRestingOrders);
    // every market's exchange snapshot, so a row never depends on the chart.
    const positionsByCoin = useTradingStore((st) => st.positionsByCoin);
    const currentTradeHash = useTradingStore((s) => s.tradeHash);
    const demoCancelledOrders = useTradingStore((s) => s.demoCancelledOrders);
    const demoHistory = useTradingStore((s) => s.tradeHistory);
    const selectedPair = useTokenPairStore((s) => s.selectedPair);
    const selectedCoin = useTokenPairStore((s) => s.selectedCoin);
    // Rows are built from stored trades, whose `pairBase` is lowercased. A row
    // is not just a label: clicking one retargets the chart, and the charting
    // library asks Hyperliquid for candles by that exact string. Canonicalise
    // once, here, so neither the terminal nor the mobile sheet can hand a
    // lowercase coin to an API that answers null for it.
    const networkId = useTokenPairStore((s) => s.networkId);
    const { data: marketData } = useTokenPairs(networkId);
    const markets = marketData?.markets;

    const { data: realTrades, isError: historyErrored } = useUserTrades();
    const { data: activeTrades } = useActiveUserTrades();

    // ── Positions rows ──────────────────────────────────────────────────
    // Ordered by OPEN TIME, newest first, and by nothing else.
    //
    // The charted market's row used to be pushed first and the rest appended, so
    // switching markets reordered the table under the user — the row they were
    // reading jumped to the top and everything shifted. A positions table is a
    // ledger: which market you happen to be looking at is not a property of the
    // positions, so it must not be a property of their order.
    const positionRows = useMemo<TerminalRow[]>(() => {
        const chartedCoin = selectedCoin ?? selectedPair.split('-')[0];

        // Demo holds a single local position with no server row behind it, plus
        // any limit orders this client is resting locally.
        if (!isRealMode) {
            const demoRows: TerminalRow[] = Object.entries(demoRestingOrders)
                .map(([coin, order]) => ({
                    key: `demo-resting-${coin}`,
                    at: order.placedAt,
                    market:
                        resolvePair(coin)?.label ?? `${coin.toUpperCase()}/USD`,
                    side: order.type,
                    leverage: `${order.leverage}x`,
                    margin: `$${order.amount.toFixed(2)}`,
                    entry: `$${priceConfig.formatDisplay(order.limitPrice)}`,
                    pnl: null,
                    pnlPositive: true,
                    status: RESTING_STATUS_META,
                    coin,
                    resting: true as const,
                    limitOrigin: true,
                    limitPrice: order.limitPrice,
                }))
                .sort((a, b) => (b.at ?? 0) - (a.at ?? 0));
            if (!activePosition) return demoRows;
            return [
                ...demoRows,
                {
                    key: 'managed',
                    limitOrigin: activePosition.limitPrice !== undefined,
                    at: timestampMs(activePosition.timestamp),
                    market:
                        resolvePair(chartedCoin)?.label ??
                        `${chartedCoin.toUpperCase()}/USD`,
                    side: activePosition.type,
                    leverage: `${activePosition.leverage}x`,
                    margin: `$${activePosition.amount.toFixed(2)}`,
                    entry: `$${priceConfig.formatDisplay(activePosition.entryPrice)}`,
                    pnl: null,
                    pnlPositive: true,
                    live: 'charted',
                    coin: chartedCoin,
                    shareDemo: true as const,
                },
            ];
        }

        const rows: TerminalRow[] = [];
        const seen = new Set<string>();

        for (const trade of activeTrades ?? []) {
            const isCharted =
                trade.pairBase.toUpperCase() === chartedCoin.toUpperCase();
            const pairInfo = resolvePair(trade.pairBase);
            const amount = tokenAmount(trade.margin, USDC_DECIMALS);
            const entry = priceAmount(trade.entryPrice);
            const leverage = leverageFromTrade(trade, amount, entry);
            const createdAt = timestampMs(trade.createdAt);
            seen.add(trade.tradeHash);

            // A resting limit order: no fill, so no entry, no qty and therefore
            // no derivable leverage — the slot this client opened it from is the
            // only place that knows it. Its price IS the limit price.
            if (trade.status === 'Resting' || (trade.status === 'Pending' && trade.limitPrice)) {
                const limitPrice = priceAmount(trade.limitPrice ?? null);
                const slotLeverage =
                    trade.leverage ??
                    positionsByCoin[trade.pairBase.toUpperCase()]?.leverage ??
                    null;
                rows.push({
                    key: trade.tradeHash,
                    at: createdAt,
                    market:
                        pairInfo?.label ??
                        `${trade.pairBase.toUpperCase()}/USD`,
                    side: trade.isLong ? 'LONG' : 'SHORT',
                    leverage: slotLeverage !== null ? `${slotLeverage}x` : '—',
                    margin: amount !== null ? `$${amount.toFixed(2)}` : '—',
                    entry:
                        limitPrice !== null
                            ? `$${priceConfig.formatDisplay(limitPrice)}`
                            : '—',
                    pnl: null,
                    pnlPositive: true,
                    status: trade.status === 'Resting' ? RESTING_STATUS_META : { label: 'SUBMITTING', className: 'text-muted-foreground' },
                    tradeHash: trade.tradeHash,
                    coin: canonicalCoin(markets, trade.pairBase),
                    ...(trade.status === 'Resting' ? { resting: true as const } : {}),
                    limitOrigin: true,
                    ...(limitPrice !== null ? { limitPrice } : {}),
                });
                continue;
            }

            // ONE source per row, whichever market is charted.
            //
            // These used to come from the exchange for the charted row and from
            // the server record for every other, so the same trade read
            // "40x $44.04" on its own market and "36x $54.47" from another. The
            // two genuinely disagree — the record stores the collateral you
            // REQUESTED while `qty` stores what actually filled, and an IOC entry
            // can fill short — so which one you saw must not depend on the chart.
            //
            // The exchange wins wherever it has answered: it reports the position
            // you actually hold. The record is the fallback until that read lands
            // (and for markets the bulk read has not covered).
            const live =
                positionsByCoin[trade.pairBase.toUpperCase()]?.livePosition ??
                null;
            const shownLeverage = live?.leverage ?? leverage;
            const shownMargin = live?.marginUsed ?? amount;
            const shownEntry = live?.entryPx ?? entry;
            rows.push({
                key: trade.tradeHash,
                limitOrigin: trade.limitPrice !== undefined && trade.limitPrice !== null,
                at: createdAt,
                market:
                    pairInfo?.label ?? `${trade.pairBase.toUpperCase()}/USD`,
                side: trade.isLong ? 'LONG' : 'SHORT',
                leverage: shownLeverage !== null ? `${shownLeverage}x` : '—',
                margin:
                    shownMargin !== null ? `$${shownMargin.toFixed(2)}` : '—',
                entry:
                    shownEntry !== null
                        ? `$${priceConfig.formatDisplay(shownEntry)}`
                        : '—',
                // rendered by a leaf cell, so this array does not rebuild on
                // every price tick.
                pnl: null,
                pnlPositive: true,
                live: isCharted ? 'charted' : 'offChart',
                tradeHash: trade.tradeHash,
                coin: canonicalCoin(markets, trade.pairBase),
                share: { kind: 'position', coin: trade.pairBase },
            });
        }

        // A position opened moments ago that the server list has not caught up
        // with. Without this the row the user just created blinks out until the
        // next refresh.
        if (
            activePosition &&
            currentTradeHash !== null &&
            !seen.has(currentTradeHash)
        ) {
            rows.push({
                key: currentTradeHash,
                limitOrigin:
                    Boolean(positionsByCoin[chartedCoin.toUpperCase()]?.limitPrice),
                at: timestampMs(activePosition.timestamp),
                market:
                    resolvePair(chartedCoin)?.label ??
                    `${chartedCoin.toUpperCase()}/USD`,
                side: activePosition.type,
                leverage: `${activePosition.leverage}x`,
                margin: `$${activePosition.amount.toFixed(2)}`,
                entry: `$${priceConfig.formatDisplay(activePosition.entryPrice)}`,
                pnl: null,
                pnlPositive: true,
                live: 'charted',
                tradeHash: currentTradeHash,
                coin: chartedCoin,
                share: { kind: 'position', coin: chartedCoin },
            });
        }

        // newest first. Ties keep insertion order, so equal timestamps do not
        // shuffle between renders.
        rows.sort((a, b) => (b.at ?? 0) - (a.at ?? 0));
        return rows;
    }, [
        markets,
        activePosition,
        positionsByCoin,
        demoRestingOrders,
        selectedPair,
        selectedCoin,
        priceConfig,
        isRealMode,
        activeTrades,
        currentTradeHash,
    ]);

    // ── History rows (closed-trade mapping);
    //    each carries a share payload for the on-demand ShareCard ─────────
    const historyRows = useMemo<TerminalRow[]>(() => {
        if (isRealMode) {
            return (realTrades ?? []).map((trade, i) => {
                const pairInfo = resolvePair(trade.pairBase);
                const amount = tokenAmount(trade.margin, USDC_DECIMALS);
                // margin is USDC base units, but pnl/prices are plain decimal
                // strings — see the encoding note on `priceAmount`.
                const pnl = priceAmount(trade.pnl);
                const entry = priceAmount(trade.entryPrice);
                const leverage = leverageFromTrade(trade, amount, entry);
                // history reads the record and only the record: a closed trade
                // has no live position to prefer.
                // A cancelled limit order never filled, so it has no entry —
                // the price it WAS resting at is the only price it ever had.
                const entryLabel = formatTradePrice(
                    entry ?? priceAmount(trade.limitPrice ?? null),
                );
                const terminalStatus = resolveTerminalStatus(trade);
                // Null for a row that never filled — exactly the rows that
                // should not offer a share button.
                const card = pnlCardFromUserTrade(trade);
                return {
                    key: trade.tradeHash ?? `${trade.createdAt}-${i}`,
                    limitOrigin: trade.limitPrice !== undefined && trade.limitPrice !== null,
                    at: timestampMs(trade.createdAt),
                    market: pairInfo?.label ?? '—',
                    side: trade.isLong ? ('LONG' as const) : ('SHORT' as const),
                    leverage: leverage !== null ? `${leverage}x` : '—',
                    margin: amount !== null ? `$${amount.toFixed(2)}` : '—',
                    entry: entryLabel !== null ? `$${entryLabel}` : '—',
                    pnl: pnl !== null ? formatPnl(pnl) : null,
                    pnlPositive: (pnl ?? 0) >= 0,
                    status: terminalStatus ? STATUS_META[terminalStatus] : null,
                    coin: canonicalCoin(markets, trade.pairBase),
                    share: card
                        ? ({ kind: 'ready', data: card } as const)
                        : undefined,
                };
            });
        }
        return [...demoHistory].reverse().map((trade: ClosedTrade, i) => ({
            key: `${trade.timestamp}-${i}`,
            limitOrigin: trade.limitPrice !== undefined && trade.limitPrice !== null,
            at: timestampMs(trade.timestamp),
            // Rows carry the market they were traded on; only rows persisted
            // before `coin` existed fall back to the current selection.
            market:
                (trade.coin ? resolvePair(trade.coin)?.label : null) ??
                selectedPair.replace('-', '/').toUpperCase(),
            side: trade.type,
            leverage: `${trade.leverage}x`,
            margin: `$${trade.amount.toFixed(2)}`,
            entry: `$${priceConfig.formatDisplay(trade.entryPrice)}`,
            pnl: `${formatPnl(trade.pnlValue)} (${trade.pnlValue >= 0 ? '+' : ''}${trade.pnlPercent.toFixed(2)}%)`,
            pnlPositive: trade.pnlValue >= 0,
            share: {
                kind: 'ready' as const,
                data: pnlCardFromClosedTrade(
                    trade,
                    selectedPair.split('-')[0] ?? '',
                ),
            },
        }));
    }, [
        markets,
        isRealMode,
        realTrades,
        demoHistory,
        selectedPair,
        priceConfig,
    ]);

    const limitRows = useMemo<TerminalRow[]>(() => {
        const active = positionRows.filter((row) => row.limitOrigin);
        const activeKeys = new Set(active.map((row) => row.key));
        const history = historyRows.filter(
            (row) => row.limitOrigin && !activeKeys.has(row.key),
        );
        const cancelled: TerminalRow[] = isRealMode
            ? []
            : demoCancelledOrders.map((order, i) => ({
                key: `demo-cancelled-${order.cancelledAt}-${i}`,
                at: order.placedAt,
                market: resolvePair(order.coin)?.label ?? `${order.coin}/USD`,
                side: order.type,
                leverage: `${order.leverage}x`,
                margin: `$${order.amount.toFixed(2)}`,
                entry: `$${priceConfig.formatDisplay(order.limitPrice)}`,
                pnl: null,
                pnlPositive: true,
                status: STATUS_META.Cancelled,
                coin: order.coin,
                limitOrigin: true,
            }));
        return [
            ...active.map((row) => ({
                ...row,
                status: row.live
                    ? { label: 'RUNNING', className: 'text-primary' }
                    : row.status,
            })),
            ...history.map((row) => ({
                ...row,
                status: row.status ?? {
                    label: 'CLOSED',
                    className: 'text-muted-foreground',
                },
            })),
            ...cancelled,
        ].sort(
            (a, b) =>
                Number(activeKeys.has(b.key)) - Number(activeKeys.has(a.key)) ||
                (b.at ?? 0) - (a.at ?? 0),
        );
    }, [
        positionRows,
        historyRows,
        isRealMode,
        demoCancelledOrders,
        priceConfig,
    ]);

    /**
     * A demo position's card is resolved here rather than in the row memo: it is
     * marked to the live price, and depending on that in the memo would rebuild
     * every row on every tick.
     */
    const resolveShare = (row: TerminalRow): PnlShareRequest | null => {
        if (!row.shareDemo) return row.share ?? null;
        const state = useTradingStore.getState();
        const position = selectActiveTradeVisual(state);
        if (!position) return null;
        return {
            kind: 'ready',
            data: pnlCardFromDemoPosition(
                selectedCoin ?? selectedPair.split('-')[0] ?? '',
                position,
                state.currentValue,
            ),
        };
    };

    return {
        positionRows: positionRows.filter((row) => !row.resting && !(row.limitOrigin && !row.live)),
        historyRows,
        limitRows,
        historyErrored: !!historyErrored,
        resolveShare,
    };
}
